<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ c("expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ c("collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ c("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!periodValid" @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ c("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!hasReport" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ c("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ c("refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <v-text-field v-model="filters.from" type="date" :label="c('from')" hide-details class="fin-field"></v-text-field>
          <v-text-field v-model="filters.to" type="date" :label="c('to')" hide-details class="fin-field"></v-text-field>
          <v-btn small color="primary" :disabled="!periodValid" @click="loadReport">{{ c("apply") }}</v-btn>
        </div>
      </div>

      <template v-if="hasReport">
        <div v-for="(warning, index) in report.warnings" :key="'w' + index" class="fin-alert-error cf-warning fr-no-print">
          <v-icon small color="#e65100">warning</v-icon>
          <span>{{ warning.text }}</span>
        </div>

        <div class="fin-summary fr-no-print">
          <div class="fin-stat">
            <span class="fin-stat-label">{{ t("netProfitBeforeTax") }}</span>
            <span class="fin-stat-value">{{ formatMoney(baseSection.net_profit.profit_before_tax) }}</span>
          </div>
          <div v-for="activity in baseSection.activities" :key="'s' + activity.key" class="fin-stat">
            <span class="fin-stat-label">{{ activityTotalLabel(activity) }}</span>
            <span class="fin-stat-value">{{ formatMoney(activity.total) }}</span>
          </div>
          <div class="fin-stat">
            <span class="fin-stat-label">{{ t("netChange") }}</span>
            <span class="fin-stat-value">{{ formatMoney(baseSection.net_change) }}</span>
          </div>
          <div class="fin-stat">
            <span class="fin-stat-label">{{ t("closingCash") }}</span>
            <span class="fin-stat-value">{{ formatMoney(baseSection.closing_cash) }}</span>
          </div>
          <span :class="report.is_reconciled ? 'fin-stat-ok' : 'fin-stat-bad'">
            <v-icon small :color="report.is_reconciled ? 'green' : 'red'">
              {{ report.is_reconciled ? "check_circle" : "error" }}
            </v-icon>
            {{ report.is_reconciled ? t("reconciled") : t("notReconciled") }}
          </span>
        </div>

        <div v-for="section in report.sections" :key="'sec' + section.currency" class="cf-section">
          <div v-if="multiCurrency" class="cf-section-title">
            {{ t("currencySection") }} {{ section.currency_label }} ({{ section.currency }})
          </div>

          <div class="cf-table-wrap fin-table-wrap">
            <table class="cf-table fin-table">
              <thead>
                <tr>
                  <th class="cf-col-label">{{ t("item") }}</th>
                  <th class="cf-col-codes-head">{{ t("linkedAccounts") }}</th>
                  <th class="fin-num-head">{{ t("openingBalance") }}</th>
                  <th class="fin-num-head">{{ t("closingBalance") }}</th>
                  <th class="fin-num-head">{{ t("change") }}</th>
                  <th class="fin-num-head">{{ t("cashEffect") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in visibleRows(section)" :key="row.id" :class="rowClass(row)">
                  <td class="cf-col-label" :title="row.title || ''">
                    <span class="cf-indent" :style="{ 'padding-inline-start': (row._level || 0) * 22 + 'px' }">
                      <v-btn v-if="row.expandable" icon small class="cf-toggle fr-no-print" @click="toggle(section, row)">
                        <v-icon small>{{ isExpanded(section, row) ? "remove" : "add" }}</v-icon>
                      </v-btn>
                      <span v-else-if="row._kind === 'detail' || row._kind === 'line'" class="cf-toggle-spacer"></span>
                      <span>{{ row.label }}</span>
                      <v-icon v-if="row.missing" small color="deep-orange" class="cf-missing" :title="t('missingCodes') + ': ' + row.missing">
                        warning
                      </v-icon>
                    </span>
                  </td>
                  <td class="cf-col-codes fin-code">{{ row.codes }}</td>
                  <td class="fin-num">{{ moneyCell(row.opening) }}</td>
                  <td class="fin-num">{{ moneyCell(row.closing) }}</td>
                  <td class="fin-num">{{ moneyCell(row.change) }}</td>
                  <td class="fin-num cf-amount">{{ moneyCell(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="cf-subtitle">{{ t("cashControlTitle") }}</div>
          <div class="cf-hint">{{ t("cashControlHint") }}</div>
          <div class="cf-table-wrap fin-table-wrap">
            <table class="cf-table fin-table">
              <thead>
                <tr>
                  <th class="cf-col-codes-head">{{ c("accountCode") }}</th>
                  <th class="cf-col-label">{{ c("accountName") }}</th>
                  <th class="fin-num-head">{{ t("openingBalance") }}</th>
                  <th class="fin-num-head">{{ t("closingBalance") }}</th>
                  <th class="fin-num-head">{{ t("change") }}</th>
                  <th>{{ t("alert") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in cashRows(section)" :key="row.id" :class="rowClass(row)">
                  <td class="cf-col-codes fin-code">{{ row.code }}</td>
                  <td class="cf-col-label">
                    <span :style="{ 'padding-inline-start': (row._level || 0) * 22 + 'px' }">{{ row.name }}</span>
                  </td>
                  <td class="fin-num">{{ moneyCell(row.opening) }}</td>
                  <td class="fin-num">{{ moneyCell(row.closing) }}</td>
                  <td class="fin-num">{{ moneyCell(row.change) }}</td>
                  <td class="cf-alert">{{ row.note }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="report.base_summary" class="cf-section">
          <div class="cf-subtitle">{{ t("baseSummaryTitle") }}</div>
          <div class="cf-table-wrap fin-table-wrap">
            <table class="cf-table fin-table">
              <thead>
                <tr>
                  <th v-for="column in summaryColumns()" :key="column.key" :class="frIsNumericColumn(column) ? 'fin-num-head' : ''">
                    {{ column.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in summaryRows()" :key="'b' + index" :class="rowClass(row)">
                  <td v-for="column in summaryColumns()" :key="column.key" :class="frIsNumericColumn(column) ? 'fin-num' : ''">
                    {{ frCellText(column, row[column.key]) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="cf-notes">
          <div class="cf-subtitle">{{ t("notesTitle") }}</div>
          <ul>
            <li v-for="note in report.notes" :key="note.code">{{ note.ar }}</li>
          </ul>
        </div>
      </template>
      <div v-else-if="!loading" class="cf-empty">{{ c("noData") }}</div>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.3 of the accountant's document 2026-10-03: cash flow statement,
 * indirect method (her formal document p7-9, worked example Docs/تشغيلي.pdf
 * p8-13). API: this.api.ACTIONS.ACCOUNTING_CASH_FLOW_STATEMENT (GET from, to,
 * format=json|csv). The backend sends STORED amounts; formatMoney / the
 * export mixin divide by 100 for display.
 *
 * One flat row list per currency section (statementRows) drives the screen
 * (rows with a parentId show only when their parent is expanded), the Excel
 * sheet and the print window, so the three never disagree.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import financeReportExport from "@/mixins/financeReportExport";
import { companyToday } from "@/util/companyTime";

const SLUG = "cash-flow-statement";

export default {
  name: "CashFlowStatement",
  title() {
    return this.$t("finance.reports2.cashFlowStatement.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent },
  mixins: [financeReportExport],
  data() {
    // Final-version doc 2026-10-05: the company (Damascus) day on the
    // server-synced clock, not the PC calendar.
    const today = companyToday();
    return {
      loading: false,
      report: null,
      expanded: {},
      filters: {
        from: today.slice(0, 4) + "-01-01",
        to: today,
      },
    };
  },
  computed: {
    hasReport() {
      return !!(this.report && this.report.sections && this.report.sections.length);
    },
    baseSection() {
      return this.hasReport ? this.report.sections[0] : null;
    },
    multiCurrency() {
      return this.hasReport && this.report.sections.length > 1;
    },
    periodValid() {
      return !!(this.filters.from && this.filters.to && this.filters.from <= this.filters.to);
    },
    isArabic() {
      return !!(this.$i18n && this.$i18n.locale === "ar");
    },
  },
  mounted() {
    this.loadReport();
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.cashFlowStatement." + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    params() {
      return { from: this.filters.from, to: this.filters.to };
    },
    loadReport() {
      if (!this.periodValid) {
        window.getApp.$emit("SHOW_TOAST", this.t("periodRequired"), "red");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_CASH_FLOW_STATEMENT, { params: this.params() })
        .then((response) => {
          this.report = (response.data && response.data.data) || null;
          this.expanded = {};
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.c("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    downloadCsv() {
      if (!this.periodValid) {
        return;
      }
      this.loading = true;
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_CASH_FLOW_STATEMENT, this.params(), SLUG).finally(() => {
        this.loading = false;
      });
    },

    // ----------------------------------------------------------------
    // Labels
    // ----------------------------------------------------------------
    pick(ar, en) {
      return this.isArabic ? ar || en || "" : en || ar || "";
    },
    lineLabel(line) {
      return this.pick(line.label_ar, line.label_en);
    },
    activityTitle(activity) {
      return this.pick(activity.title_ar, activity.title_en);
    },
    activityTotalLabel(activity) {
      return this.pick(activity.total_ar, activity.total_en);
    },
    reportLabel(key, fallbackKey) {
      const labels = (this.report && this.report.labels) || {};
      return this.isArabic && labels[key] ? labels[key] : this.t(fallbackKey);
    },
    moneyCell(value) {
      return value === null || value === undefined ? "" : this.formatMoney(value);
    },

    // ----------------------------------------------------------------
    // Rows (screen, Excel and print share them)
    // ----------------------------------------------------------------
    statementRows(section) {
      const rows = [];
      const isChange = (line) => line.basis === "balance_change";

      section.activities.forEach((activity) => {
        rows.push({ id: "act:" + activity.key, label: this.activityTitle(activity), _kind: "section", _level: 0 });

        activity.groups.forEach((group) => {
          if (group.title_ar) {
            rows.push({ id: "grp:" + group.key, label: group.title_ar, _kind: "header", _level: 0 });
          }
          group.rows.forEach((line) => {
            const lineId = "line:" + line.key;
            const children = [];
            if (line.basis === "income_statement") {
              children.push({ label: this.t("revenue"), amount: line.revenue });
              children.push({ label: this.t("expenses"), amount: -line.expenses });
            }
            (line.accounts || []).forEach((account) => {
              children.push({
                label: account.name + (account.is_add_back ? " *" : ""),
                codes: account.code,
                opening: isChange(line) ? account.opening_balance : null,
                closing: isChange(line) ? account.closing_balance : null,
                change: isChange(line) ? account.change : null,
                amount: account.amount,
                title: account.is_add_back ? this.t("addBack") : "",
              });
            });
            if (Math.abs(Number(line.non_cash_offset) || 0) >= 0.005) {
              children.push({ label: this.t("nonCashOffset"), amount: line.non_cash_offset, _bold: true });
            }

            rows.push({
              id: lineId,
              label: this.lineLabel(line),
              codes: (line.codes || []).join(" "),
              opening: isChange(line) ? line.opening_balance : null,
              closing: isChange(line) ? line.closing_balance : null,
              change: isChange(line) ? line.change : null,
              amount: line.amount,
              missing: (line.missing_codes || []).join(" "),
              expandable: children.length > 0,
              _kind: "line",
              _level: 1,
              _bold: line.basis === "income_statement",
            });
            children.forEach((child, index) => {
              rows.push(
                Object.assign({ id: lineId + ":" + index, parentId: lineId, _kind: "detail", _level: 2 }, child)
              );
            });
          });

          if (group.total !== undefined && group.total !== null) {
            rows.push({ id: "gt:" + group.key, label: group.total_ar, amount: group.total, _kind: "subtotal", _level: 0 });
          }
          if (group.running_total !== undefined && group.running_total !== null) {
            rows.push({
              id: "rt:" + group.key,
              label: group.running_total_ar,
              amount: group.running_total,
              _kind: "subtotal",
              _level: 0,
            });
          }
        });

        rows.push({
          id: "tot:" + activity.key,
          label: this.activityTotalLabel(activity),
          amount: activity.total,
          _kind: "total",
          _level: 0,
        });
      });

      const unclassified = section.unclassified || { accounts: [], amount: 0 };
      const unclassifiedId = "unclassified";
      rows.push({
        id: unclassifiedId,
        label: this.isArabic && unclassified.label_ar ? unclassified.label_ar : this.t("unclassified"),
        amount: unclassified.amount,
        expandable: unclassified.accounts.length > 0,
        _kind: "line",
        _level: 0,
        _bold: true,
        _highlight: unclassified.accounts.length ? "warn" : null,
      });
      unclassified.accounts.forEach((account, index) => {
        rows.push({
          id: unclassifiedId + ":" + index,
          parentId: unclassifiedId,
          label: account.name,
          codes: account.code || "",
          amount: account.amount,
          _kind: "detail",
          _level: 2,
        });
      });

      if (Math.abs(Number(section.fx_difference) || 0) >= 0.005) {
        rows.push({
          id: "fx",
          label: this.reportLabel("fx_difference", "fxDifference"),
          amount: section.fx_difference,
          _kind: "line",
          _level: 0,
          _bold: true,
        });
      }

      rows.push({ id: "net", label: this.reportLabel("net_change", "netChange"), amount: section.net_change, _kind: "total", _level: 0 });
      rows.push({
        id: "open",
        label: this.reportLabel("opening_cash", "openingCash"),
        codes: (section.cash_codes || []).join(" "),
        amount: section.opening_cash,
        _kind: "opening",
        _level: 0,
      });
      rows.push({
        id: "close",
        label: this.reportLabel("closing_cash", "closingCash"),
        amount: section.closing_cash,
        _kind: "closing",
        _level: 0,
      });
      rows.push({
        id: "actual",
        label: this.reportLabel("actual_closing_cash", "actualClosingCash"),
        amount: section.actual_closing_cash,
        _kind: "closing",
        _level: 0,
      });
      rows.push({
        id: "diff",
        label: this.reportLabel("control_difference", "controlDifference"),
        amount: section.control_difference,
        _kind: "total",
        _level: 0,
        _highlight: section.is_reconciled ? "ok" : "error",
      });

      return rows;
    },
    cashRows(section) {
      const rows = (section.cash_accounts || []).map((account) => ({
        id: "cash:" + account.id,
        code: account.code,
        name: account.name,
        opening: account.opening,
        closing: account.closing,
        change: account.change,
        note: account.alert ? account.alert.text : "",
        _kind: account.is_group ? "subtotal" : "detail",
        _level: account.depth,
        _highlight: account.alert ? "error" : null,
      }));
      rows.push({
        id: "cash:total",
        code: "",
        name: this.reportLabel("actual_closing_cash", "actualClosingCash"),
        opening: section.opening_cash,
        closing: section.actual_closing_cash,
        change: section.actual_closing_cash - section.opening_cash,
        note: section.is_reconciled ? this.t("reconciled") : this.t("notReconciled"),
        _kind: "total",
        _level: 0,
        _highlight: section.is_reconciled ? "ok" : "error",
      });
      return rows;
    },
    summaryRows() {
      const summary = this.report && this.report.base_summary;
      if (!summary) {
        return [];
      }
      const rows = summary.rows.map((row) => ({
        currency: row.currency_label + " (" + row.currency + ")",
        rate: row.rate,
        opening_base: row.opening_base,
        net_change_base: row.net_change_base,
        closing_base: row.closing_base,
        _kind: "detail",
      }));
      rows.push({
        currency: this.reportLabel("fx_difference", "fxDifference"),
        rate: null,
        opening_base: null,
        net_change_base: summary.fx_difference,
        closing_base: null,
        _kind: "subtotal",
      });
      rows.push({
        currency: this.c("total"),
        rate: null,
        opening_base: summary.opening_base,
        net_change_base: summary.net_change_base,
        closing_base: summary.closing_base,
        _kind: "total",
      });
      return rows;
    },
    visibleRows(section) {
      const open = this.expanded[section.currency] || {};
      return this.statementRows(section).filter((row) => !row.parentId || open[row.parentId]);
    },
    isExpanded(section, row) {
      return !!(this.expanded[section.currency] || {})[row.id];
    },
    toggle(section, row) {
      const current = Object.assign({}, this.expanded[section.currency] || {});
      if (current[row.id]) {
        delete current[row.id];
      } else {
        current[row.id] = true;
      }
      this.expanded = Object.assign({}, this.expanded, { [section.currency]: current });
    },
    expandAll() {
      const next = {};
      (this.report.sections || []).forEach((section) => {
        const open = {};
        this.statementRows(section).forEach((row) => {
          if (row.expandable) {
            open[row.id] = true;
          }
        });
        next[section.currency] = open;
      });
      this.expanded = next;
    },
    collapseAll() {
      this.expanded = {};
    },
    rowClass(row) {
      const classes = ["cf-k-" + (row._kind || "detail")];
      if (row._highlight) classes.push("cf-hl-" + row._highlight);
      if (row._bold) classes.push("cf-bold");
      return classes;
    },

    // ----------------------------------------------------------------
    // Exports
    // ----------------------------------------------------------------
    statementColumns() {
      return [
        { key: "label", label: this.t("item"), type: "text", width: 52, indent: true },
        { key: "codes", label: this.t("linkedAccounts"), type: "code", width: 26 },
        { key: "opening", label: this.t("openingBalance"), type: "money" },
        { key: "closing", label: this.t("closingBalance"), type: "money" },
        { key: "change", label: this.t("change"), type: "money" },
        { key: "amount", label: this.t("cashEffect"), type: "money" },
      ];
    },
    cashColumns() {
      return [
        { key: "code", label: this.c("accountCode"), type: "code", width: 14 },
        { key: "name", label: this.c("accountName"), type: "text", width: 40, indent: true },
        { key: "opening", label: this.t("openingBalance"), type: "money" },
        { key: "closing", label: this.t("closingBalance"), type: "money" },
        { key: "change", label: this.t("change"), type: "money" },
        { key: "note", label: this.t("alert"), type: "text", width: 24 },
      ];
    },
    summaryColumns() {
      return [
        { key: "currency", label: this.c("currency"), type: "text", width: 22 },
        { key: "rate", label: this.t("rate"), type: "number" },
        { key: "opening_base", label: this.t("openingBalance"), type: "money" },
        { key: "net_change_base", label: this.t("netChange"), type: "money" },
        { key: "closing_base", label: this.t("closingBalance"), type: "money" },
      ];
    },
    noteTexts() {
      const warnings = (this.report.warnings || []).map((w) => this.t("warnings") + ": " + w.text);
      const notes = (this.report.notes || []).map((n) => n.ar);
      return warnings.concat(notes);
    },
    sectionTitle(section, suffix) {
      const parts = [];
      if (suffix) parts.push(suffix);
      if (this.multiCurrency) parts.push(section.currency_label + " (" + section.currency + ")");
      return parts.join(" - ");
    },
    exportExcel() {
      if (!this.hasReport) {
        return;
      }
      const sheets = [];
      this.report.sections.forEach((section) => {
        sheets.push({
          name: this.t("statementSheet") + (this.multiCurrency ? " " + section.currency : ""),
          title: this.multiCurrency ? this.sectionTitle(section) : undefined,
          columns: this.statementColumns(),
          rows: this.statementRows(section),
          notes: this.noteTexts(),
        });
        sheets.push({
          name: this.t("cashSheet") + (this.multiCurrency ? " " + section.currency : ""),
          title: this.sectionTitle(section, this.t("cashControlTitle")),
          columns: this.cashColumns(),
          rows: this.cashRows(section),
        });
      });
      if (this.report.base_summary) {
        sheets.push({
          name: this.t("summarySheet"),
          title: this.t("baseSummaryTitle"),
          columns: this.summaryColumns(),
          rows: this.summaryRows(),
        });
      }
      this.frExportXlsx({ fileName: SLUG, header: this.report.header, sheets: sheets });
    },
    exportPrint() {
      if (!this.hasReport) {
        return;
      }
      const sections = [];
      this.report.sections.forEach((section) => {
        sections.push({
          title: this.multiCurrency ? this.sectionTitle(section) : null,
          columns: this.statementColumns(),
          rows: this.statementRows(section),
        });
        sections.push({
          title: this.sectionTitle(section, this.t("cashControlTitle")),
          columns: this.cashColumns(),
          rows: this.cashRows(section),
          notes: [this.t("cashControlHint")],
        });
      });
      if (this.report.base_summary) {
        sections.push({ title: this.t("baseSummaryTitle"), columns: this.summaryColumns(), rows: this.summaryRows() });
      }
      this.frPrint({ header: this.report.header, sections: sections, notes: this.noteTexts(), orientation: "portrait" });
    },
  },
};
</script>

<style scoped>
.cf-section {
  padding: 0 8px 8px;
}
.cf-section-title {
  font-weight: 700;
  font-size: 14px;
  padding: 8px 4px 4px;
}
.cf-subtitle {
  font-weight: 700;
  font-size: 13.5px;
  padding: 10px 4px 2px;
}
.cf-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  padding: 0 4px 4px;
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.cf-table-wrap {
  min-height: 0;
  margin: 0;
}
.cf-col-label {
  min-width: 320px;
  white-space: normal !important;
}
/* Linked account codes: LTR digits (fin-code) that may wrap. */
.cf-col-codes {
  color: rgba(0, 0, 0, 0.65);
  font-size: 12px;
  white-space: normal !important;
  max-width: 260px;
}
.cf-col-codes-head {
  max-width: 260px;
  white-space: normal !important;
}
.cf-amount {
  font-weight: 600;
}
.cf-alert {
  color: #c62828;
  white-space: normal !important;
}
.cf-indent {
  display: inline-flex;
  align-items: center;
}
.cf-toggle {
  margin: 0;
  margin-inline-end: 4px;
  height: 22px;
  width: 22px;
}
.cf-toggle-spacer {
  display: inline-block;
  width: 26px;
}
.cf-missing {
  margin-inline-start: 6px;
}
.cf-k-section td {
  background: #e3f2fd;
  font-weight: 700;
}
.cf-k-header td {
  background: #f5f7fa;
  font-weight: 700;
}
.cf-k-subtotal td,
.cf-k-opening td,
.cf-k-closing td,
.cf-bold td {
  background: #f5f5f5;
  font-weight: 700;
}
.cf-k-total td {
  background: #e8f5e9;
  font-weight: 700;
}
.cf-k-detail td {
  color: rgba(0, 0, 0, 0.72);
  font-size: 12.5px;
}
.cf-hl-warn td {
  background: #fff3e0;
}
.cf-hl-error td {
  background: #ffcdd2;
}
.cf-hl-ok td {
  background: #e8f5e9;
}
.cf-warning {
  background: #fff3e0;
  color: #bf360c;
  border-inline-start-color: #ef6c00;
}
.cf-notes {
  padding: 4px 12px 16px;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.7);
}
.cf-notes ul {
  margin: 0;
  padding-inline-start: 18px;
}
.cf-empty {
  padding: 32px;
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
